import React, { useState, useEffect, useMemo, useCallback } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import {
  Row,
  Col,
  Typography,
  Select,
  Input,
  Radio,
  Alert,
  Skeleton,
  Pagination,
  Empty,
} from "antd";
import moment from "moment-jalaali";
import DatePicker from "@general/datePicker";
import getWindowSize from "@utils/windowSize";
import MyServicesTable from "./table";
import UserServiceCard from "./serviceCard";
import UserServiceDetailsDrawer from "./serviceDetailsDrawer";
import {
  SERVICE_TYPE_OPTIONS,
  SERVICE_STATUS_OPTIONS,
} from "@staticData/services";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { Option } = Select;

export const GET_USER_SERVICES = gql`
  query GetUserServices(
    $paginateInput: PaginateInput!
    $searchInput: UserServiceSearchInput
  ) {
    getUserServicesCount(searchInput: $searchInput)
    getUserServices(paginateInput: $paginateInput, searchInput: $searchInput) {
      id
      serviceType
      status
      serviceName
      amount
      createdAt
      startAt
      endAt
      description
      contract
      productId
      product {
        id
        name
        category
      }
      packageId
      packageName
      packageDays
      packageSessions
      meetingRoomName
      workAt
      startTime
      endTime
      capacity
      reserve {
        id
        startAt
        endAt
      }
    }
  }
`;

function UserMyServices() {
  const [activeTab, setActiveTab] = useState("ALL"); // ALL | CURRENT | HISTORY
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const [selectedService, setSelectedService] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [windowDimensions, setWindowDimensions] = useState(getWindowSize());

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowSize());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowDimensions.innerWidth < 768;

  const [fetchServices, { data, loading, error }] = useLazyQuery(
    GET_USER_SERVICES,
    {
      fetchPolicy: "network-only",
    }
  );

  const searchInput = useMemo(() => {
    const input = {};

    if (selectedType) {
      input.serviceType = [selectedType];
    }

    if (activeTab === "CURRENT") {
      input.status = ["ACTIVE", "PENDING_START", "NEAR_EXPIRY"];
    } else if (activeTab === "HISTORY") {
      input.status = ["EXPIRED"];
    } else if (selectedStatus) {
      input.status = [selectedStatus];
    }

    if (searchPhrase && searchPhrase.trim()) {
      input.textSearch = searchPhrase.trim();
    }

    if (dateRange.from && dateRange.to) {
      input.createdAt = [dateRange.from, dateRange.to];
    }

    return input;
  }, [selectedType, activeTab, selectedStatus, searchPhrase, dateRange]);

  const loadData = useCallback(() => {
    fetchServices({
      variables: {
        paginateInput: {
          page: currentPage,
          perPage: 10,
          sort: sortColumn,
          sortDir: sortDir,
        },
        searchInput,
      },
    });
  }, [fetchServices, currentPage, sortColumn, sortDir, searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTabChange = (e) => {
    setActiveTab(e.target.value);
    setSelectedStatus(null);
    setCurrentPage(1);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value || null);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value || null);
    if (value) {
      setActiveTab("ALL");
    }
    setCurrentPage(1);
  };

  const handleSearch = (phrase) => {
    setSearchPhrase(phrase);
    setCurrentPage(1);
  };

  const handleDateFilter = (dates, dateStrings) => {
    setCurrentPage(1);
    if (!dateStrings || !dateStrings[0] || !dateStrings[1]) {
      setDateRange({ from: null, to: null });
    } else {
      const from = moment(dateStrings[0], "jYYYY/jMM/jDD")
        .startOf("day")
        .toDate()
        .toISOString();
      const to = moment(dateStrings[1], "jYYYY/jMM/jDD")
        .endOf("day")
        .toDate()
        .toISOString();
      setDateRange({ from, to });
    }
  };

  const handleSort = (sorter) => {
    setCurrentPage(1);
    if (!sorter || !sorter.order) {
      setSortColumn("createdAt");
      setSortDir("desc");
      return;
    }
    setSortColumn(sorter.columnKey || "createdAt");
    setSortDir(sorter.order === "ascend" ? "asc" : "desc");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOpenDetails = (service) => {
    setSelectedService(service);
    setDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDrawerOpen(false);
  };

  const servicesList = useMemo(() => data?.getUserServices || [], [data]);
  const totalCount = data?.getUserServicesCount || 0;

  const hasNearExpiry = useMemo(() => {
    return servicesList.some((s) => s.status === "NEAR_EXPIRY");
  }, [servicesList]);

  return (
    <div style={{ padding: "0 0 40px 0" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            سرویس‌های من
          </Title>
        </Col>
      </Row>

      {hasNearExpiry && (
        <Alert
          message="برخی از سرویس‌های شما در آستانه انقضا هستند."
          description="لطفاً در صورت تمایل نسبت به تمدید سرویس‌های مورد نیاز خود اقدام نمایید."
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 20 }}
        />
      )}

      {error && (
        <Alert
          message="خطا در دریافت اطلاعات سرویس‌ها"
          description={error.message}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      {/* Filter bar */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: 18,
          borderRadius: 8,
          marginBottom: 20,
          border: "1px solid #f0f0f0",
        }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10} lg={8}>
            <Radio.Group
              value={activeTab}
              onChange={handleTabChange}
              buttonStyle="solid"
              style={{ width: "100%", display: "flex" }}
            >
              <Radio.Button value="ALL" style={{ flex: 1, textAlign: "center" }}>
                همه سرویس‌ها
              </Radio.Button>
              <Radio.Button
                value="CURRENT"
                style={{ flex: 1, textAlign: "center" }}
              >
                جاری
              </Radio.Button>
              <Radio.Button
                value="HISTORY"
                style={{ flex: 1, textAlign: "center" }}
              >
                سوابق
              </Radio.Button>
            </Radio.Group>
          </Col>

          <Col xs={12} sm={8} md={6} lg={4}>
            <Select
              allowClear
              placeholder="نوع سرویس"
              value={selectedType}
              onChange={handleTypeChange}
              style={{ width: "100%" }}
            >
              {SERVICE_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={12} sm={8} md={6} lg={4}>
            <Select
              allowClear
              placeholder="وضعیت"
              value={selectedStatus}
              onChange={handleStatusChange}
              disabled={activeTab !== "ALL"}
              style={{ width: "100%" }}
            >
              {SERVICE_STATUS_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={8} md={8} lg={4}>
            <Search
              placeholder="جستجو در سرویس‌ها"
              onSearch={handleSearch}
              allowClear
              enterButton
            />
          </Col>

          <Col xs={24} md={12} lg={4}>
            <RangePicker
              placeholder={["از تاریخ خرید", "تا تاریخ"]}
              onChange={handleDateFilter}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      </div>

      {/* Content area */}
      {loading && !data ? (
        <div style={{ backgroundColor: "#fff", padding: 24, borderRadius: 8 }}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      ) : isMobile ? (
        <div>
          {servicesList.length === 0 ? (
            <div
              style={{
                backgroundColor: "#fff",
                padding: 40,
                borderRadius: 8,
                textAlign: "center",
              }}
            >
              <Empty description="هیچ سرویسی یافت نشد" />
            </div>
          ) : (
            <>
              {servicesList.map((service) => (
                <UserServiceCard
                  key={service.id}
                  service={service}
                  onOpenDetails={handleOpenDetails}
                />
              ))}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <Pagination
                  current={currentPage}
                  total={totalCount}
                  pageSize={10}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#fff",
            padding: 20,
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
        >
          <MyServicesTable
            services={servicesList}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={10}
            loading={loading}
            onPageChange={handlePageChange}
            onSort={handleSort}
            onOpenDetails={handleOpenDetails}
          />
        </div>
      )}

      {/* Details Drawer */}
      <UserServiceDetailsDrawer
        open={drawerOpen}
        onClose={handleCloseDetails}
        service={selectedService}
        windowSize={windowDimensions.innerWidth}
      />
    </div>
  );
}

export default UserMyServices;
